// The editor cache stores the exact actor-preserving WAV, not a legacy worker render.
import { languageOptions } from './speech-languages.ts';
export const PREVIEW_AUDIO_PIPELINE_REVISION = "actor-ted-pitch-preserving-v4";
const MAX_CACHED_WAVE_BYTES = 20 * 1024 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function canonicalPreviewJson(value: unknown): string {
  const canonical = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(canonical);
    if (item && typeof item === "object") {
      return Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b))
        .map(([key, child]) => [key, canonical(child)]));
    }
    return item;
  };
  return JSON.stringify(canonical(value));
}

export interface PreviewCacheInput {
  userId: string;
  voiceId: string;
  voiceRevision: string;
  referencePath: string;
  referenceText: string;
  voiceSettings: unknown;
  text: string;
  synthesisOptions: unknown;
  modelRevision: string;
  pipelineRevision?: string;
}

export async function previewCachePath(input: PreviewCacheInput): Promise<string> {
  if (!UUID.test(input.userId) || !UUID.test(input.voiceId) || !input.voiceRevision) {
    throw new Error("Preview cache requires an authenticated owner and a versioned voice");
  }
  const pipelineRevision = input.pipelineRevision ?? PREVIEW_AUDIO_PIPELINE_REVISION;
  if (!/^[a-z0-9-]+$/.test(pipelineRevision)) throw new Error("Invalid preview pipeline revision");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(
    canonicalPreviewJson({ ...input, pipelineRevision }),
  ));
  const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
  return `${input.userId}/editor-previews/${pipelineRevision}/${hash}.wav`;
}

export function previewSessionCacheKey(input: {
  userId: string; voiceId: string; voiceRevision: string; text: string; synthesisOptions: unknown;
}): string {
  return canonicalPreviewJson({ ...input, pipelineRevision: PREVIEW_AUDIO_PIPELINE_REVISION });
}

export function isCacheablePreviewWave(bytes: Uint8Array): boolean {
  if (bytes.byteLength <= 44 || bytes.byteLength > MAX_CACHED_WAVE_BYTES) return false;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" &&
    String.fromCharCode(...bytes.slice(8, 12)) === "WAVE" &&
    view.getUint32(4, true) + 8 === bytes.byteLength;
}

export interface CacheStorage {
  download(path: string): PromiseLike<{ data: Blob | null; error: unknown }>;
  upload(path: string, bytes: Uint8Array, options: { contentType: string; upsert: boolean; cacheControl: string }):
    PromiseLike<{ error: unknown }>;
}

// The same neutral key is used by ordinary playback and emotional preparation.
// Deliberately retain the existing pipeline namespace so old compatible WAVs work.
export const neutralPreviewCachePath = (input: PreviewCacheInput): Promise<string> =>
  previewCachePath({ ...input, synthesisOptions: languageOptions(input.synthesisOptions as Record<string, unknown>) });

type NeutralResult = { bytes: Uint8Array; cache: "hit" | "miss" | "bypass" };
// Coalesce within a warm Edge isolate. Durable storage handles later requests;
// this is not a distributed lock and does not claim cross-isolate exactly-once.
const neutralInFlight = new Map<string, Promise<NeutralResult>>();

export async function cachedNeutralPreview(
  storage: CacheStorage,
  path: string,
  generate: () => Promise<Uint8Array>,
): Promise<NeutralResult> {
  const existing = neutralInFlight.get(path);
  if (existing) return existing;
  const work = (async (): Promise<NeutralResult> => {
    const cached = await readPreviewCache(storage, path);
    if (cached) return { bytes: cached, cache: "hit" };
    const bytes = await generate();
    if (!isCacheablePreviewWave(bytes)) throw new Error("Invalid neutral preview waveform");
    const saved = await writePreviewCache(storage, path, bytes);
    return { bytes, cache: saved ? "miss" : "bypass" };
  })();
  neutralInFlight.set(path, work);
  try { return await work; }
  finally { if (neutralInFlight.get(path) === work) neutralInFlight.delete(path); }
}

// Bind timings to the exact PCM file, not just text or duration. A repaired WAV
// must never reuse stale offsets, even if it has the same number of samples.
export async function previewTimingCachePath(neutralPath: string, bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(bytes));
  const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
  return `${neutralPath.replace(/\.wav$/, "")}.word-timings-v1-${hash}.json`;
}

export async function cachedPreviewTimings<T>(
  storage: CacheStorage,
  path: string,
  validate: (value: unknown) => T,
  generate: () => Promise<unknown>,
): Promise<{ manifest: T; cache: "hit" | "miss" | "bypass" }> {
  try {
    const { data, error } = await storage.download(path);
    if (!error && data && data.size <= 256 * 1024) {
      return { manifest: validate(JSON.parse(await data.text())), cache: "hit" };
    }
  } catch { /* Corrupt or unavailable cache: generate and validate fresh timings. */ }
  const manifest = validate(await generate());
  const bytes = new TextEncoder().encode(JSON.stringify(manifest));
  try {
    if (bytes.byteLength > 256 * 1024) return { manifest, cache: "bypass" };
    const { error } = await storage.upload(path, bytes, {
      contentType: "application/json", upsert: true, cacheControl: "0",
    });
    return { manifest, cache: error ? "bypass" : "miss" };
  } catch { return { manifest, cache: "bypass" }; }
}

export async function readPreviewCache(storage: CacheStorage, path: string): Promise<Uint8Array | null> {
  try {
    const { data, error } = await storage.download(path);
    if (error || !data || data.size > MAX_CACHED_WAVE_BYTES) return null;
    const bytes = new Uint8Array(await data.arrayBuffer());
    return isCacheablePreviewWave(bytes) ? bytes : null;
  } catch {
    // Cache outages must not prevent normal generation.
    return null;
  }
}

export async function writePreviewCache(storage: CacheStorage, path: string, bytes: Uint8Array): Promise<boolean> {
  if (!isCacheablePreviewWave(bytes)) return false;
  try {
    // This deterministic, owner-scoped cache path is safe to repair if corrupted.
    const { error } = await storage.upload(path, bytes, { contentType: "audio/wav", upsert: true, cacheControl: "0" });
    return !error;
  } catch {
    return false;
  }
}
